import type { AxiosError } from 'axios';
import { authClient } from './client';
import type {
  AdminInventoryListPayload,
  AdminInventoryOrderBy,
  AdminInventoryOrderDirection,
  AdminInventoryRow,
  AdminInventorySummary,
  InventoryStatus,
} from '../types/inventory';
import { INVENTORY_STATUS_OPTIONS } from '../types/inventory';
import downloadBlob from '../utils/downloadBlob';

/**
 * Expected backend contract (implement on your API):
 *
 * ### `GET /api/admin/inventory`
 * Query (all optional except pagination):
 * - `page` (number, default 1)
 * - `pageSize` (number, default 25)
 * - `name` — partial match on product title
 * - `category`
 * - `size` — CM value as stored (string match)
 * - `condition`
 * - `consignor` — partial match on consignor display name
 * - `consignorId` — exact consignor id (use when filtering from a row click)
 * - `sku` — matches both **sku** and **artId** (UU ID) fields
 * - `status` — `Activo` | `Vendido` | `Inactivo` | `Incompleto` | `Devuelto` | `Devolución pendiente` | `Recogida pendiente`
 *   (`Recogida pendiente`: inventario pendiente de recogida por consignador desactivado, p. ej. `consignorDeactivatedAt` definido.)
 * - `orderBy` — `id` | `title` | `cost` | `price` | `size`
 * - `orderDirection` — `asc` | `desc`
 *
 * Response JSON (matches other endpoints using `{ data: ... }`):
 * ```json
 * {
 *   "data": {
 *     "items": [
 *       {
 *         "id": 1,
 *         "artId": "UU-00042",
 *         "title": "Nike Dunk Low",
 *         "imageUrl": "https://...",
 *         "category": "Men's Lifestyle",
 *         "sku": "DD1391-100",
 *         "consignorId": 12,
 *         "consignorName": "Carlos Rodriguez",
 *         "sizeCm": "27.5",
 *         "condition": "DS",
 *         "cost": 2950,
 *         "price": 3400,
 *         "status": "Activo"
 *       }
 *     ],
 *     "total": 482,
 *     "page": 1,
 *     "pageSize": 25,
 *     "summary": {
 *       "totalCost": 1240500,
 *       "totalPrice": 1450000,
 *       "availableCount": 410,
 *       "totalCount": 482
 *     }
 *     (availableCount = filas en estado Activo)
 *   }
 * }
 * ```
 * `summary` is optional; if omitted the UI shows totals only for the current page or hides cards.
 *
 * ### `GET /api/admin/inventory/export`
 * Same **filter and sort** query params as above (omit `page` / `pageSize`). Returns **all** matching rows.
 * - `Content-Type: text/csv; charset=utf-8`
 * - Optional: `Content-Disposition: attachment; filename="inventario.csv"`
 *
 * Suggested CSV header: `id,artId,title,category,sku,consignorId,consignorName,sizeCm,condition,cost,price,status`
 */

export interface AdminInventoryListQuery {
  page: number;
  pageSize: number;
  orderBy: AdminInventoryOrderBy;
  orderDirection: AdminInventoryOrderDirection;
  categoryId?: number;
  brandId?: number;
  modelId?: number;
  sizeId?: number;
  conditionId?: number;
  productId?: number;
  name?: string;
  model?: string;
  category?: string;
  brand?: string;
  size?: string;
  condition?: string;
  consignor?: string;
  consignorId?: number;
  sku?: string;
  status?: InventoryStatus | '';
}

export interface InventoryFilterOption {
  id: number;
  label: string;
}

export interface InventoryModelOption {
  id: number;
  label: string;
}

export interface InventoryConsignorOption {
  id: number;
  fullName: string;
}

export interface InventoryProductSearchOption {
  id: number;
  title: string;
  brandId?: number | null;
  categoryId?: number | null;
  modelId?: number | null;
  brand?: string | null;
  category?: string | null;
  model?: string | null;
  thumbnailUrl?: string | null;
}

export interface InventoryProductPhoto {
  id: number;
  angle: string;
  url: string;
}

export interface InventoryExtraPhoto {
  id: number;
  url: string;
  displayOrder?: number;
}

/** Untagged images from Shopify sync; read-only until re-uploaded with an angle. */
export type InventoryLegacyPhoto = InventoryExtraPhoto;

export interface InventoryEditProductFormData {
  productId: number | null;
  brandId: number;
  categoryId: number;
  modelId: number;
  modelLabel: string;
  colorway: string;
  sku: string;
  description: string;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  photos: InventoryProductPhoto[];
  extraPhotos: InventoryExtraPhoto[];
  legacyPhotos: InventoryLegacyPhoto[];
}

export interface InventoryConditionPhotoSlot {
  id: number | null;
  angle: string;
  url: string | null;
  displayOrder: number;
}

function emptyPayload(page: number, pageSize: number): AdminInventoryListPayload {
  return { items: [], total: 0, page, pageSize };
}

function parseSummary(raw: unknown): AdminInventorySummary | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const s = raw as Record<string, unknown>;
  const totalCost = Number(s.totalCost);
  const totalPrice = Number(s.totalPrice);
  const availableCount = Number(s.availableCount);
  const totalCount = Number(s.totalCount);
  if (
    !Number.isFinite(totalCost) ||
    !Number.isFinite(totalPrice) ||
    !Number.isFinite(availableCount) ||
    !Number.isFinite(totalCount)
  ) {
    return undefined;
  }
  return { totalCost, totalPrice, availableCount, totalCount };
}

const LEGACY_STATUS_MAP: Record<string, InventoryStatus> = {
  Available: 'Activo',
  Sold: 'Vendido',
  sold: 'Vendido',
  Draft: 'Incompleto',
  Inactive: 'Inactivo',
  available: 'Activo',
  vendido: 'Vendido',
  draft: 'Incompleto',
  inactive: 'Inactivo',
  Returned: 'Devuelto',
  returned: 'Devuelto',
};

/** Sin tilde en API → valor canónico en UI. */
const STATUS_ALIASES: Record<string, InventoryStatus> = {
  'Devolucion pendiente': 'Devolución pendiente',
  'devolucion pendiente': 'Devolución pendiente',
  'DEVOLUCION PENDIENTE': 'Devolución pendiente',
};

function parseStatus(raw: unknown): InventoryStatus {
  const st = String(raw ?? '').trim();
  const aliased = STATUS_ALIASES[st];
  if (aliased !== undefined) {
    return aliased;
  }
  if (INVENTORY_STATUS_OPTIONS.includes(st as InventoryStatus)) {
    return st as InventoryStatus;
  }
  if (LEGACY_STATUS_MAP[st] !== undefined) {
    return LEGACY_STATUS_MAP[st];
  }
  return 'Incompleto';
}

function parseBooleanish(raw: unknown): boolean {
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw === 1;
  const t = String(raw ?? '')
    .trim()
    .toLowerCase();
  return t === 'true' || t === '1' || t === 'yes' || t === 'si';
}

function parseRow(raw: unknown): AdminInventoryRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id)) return null;
  const cost = Number(r.cost);
  const price = Number(r.price);
  return {
    id,
    artId: (() => {
      const v = r.artId ?? r.art_id;
      if (v == null || v === '') return null;
      const s = String(v).trim();
      return s || null;
    })(),
    handle: (() => {
      const v = r.handle;
      if (v == null || v === '') return null;
      const s = String(v).trim();
      return s || null;
    })(),
    title: String(r.title ?? ''),
    imageUrl: typeof r.imageUrl === 'string' && r.imageUrl ? r.imageUrl : null,
    category: typeof r.category === 'string' && r.category ? r.category : null,
    sku: typeof r.sku === 'string' && r.sku ? r.sku : null,
    consignorId: Number.isFinite(Number(r.consignorId)) ? Number(r.consignorId) : 0,
    consignorName: String(r.consignorName ?? ''),
    sizeCm: r.sizeCm != null && String(r.sizeCm).trim() ? String(r.sizeCm) : null,
    condition:
      typeof r.condition === 'string' && r.condition.trim()
        ? r.condition.trim()
        : 'Sin especificar',
    cost: Number.isFinite(cost) ? cost : 0,
    price: Number.isFinite(price) ? price : 0,
    status: parseStatus(r.status),
    shopifyActive: parseBooleanish(
      r.shopifyActive ?? r.isShopifyActive ?? r.shopify_active ?? r.is_shopify_active
    ),
  };
}

function parseListPayload(
  body: unknown,
  fallbackPage: number,
  fallbackPageSize: number
): AdminInventoryListPayload {
  if (!body || typeof body !== 'object') {
    return emptyPayload(fallbackPage, fallbackPageSize);
  }
  const o = body as Record<string, unknown>;
  const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
    string,
    unknown
  >;
  const rawItems = inner.items;
  const items = Array.isArray(rawItems)
    ? rawItems.map(parseRow).filter((row): row is AdminInventoryRow => row !== null)
    : [];
  const total =
    typeof inner.total === 'number' && Number.isFinite(inner.total) ? inner.total : items.length;
  const page = typeof inner.page === 'number' && inner.page >= 1 ? inner.page : fallbackPage;
  const pageSize =
    typeof inner.pageSize === 'number' && inner.pageSize >= 1 ? inner.pageSize : fallbackPageSize;
  return {
    items,
    total,
    page,
    pageSize,
    summary: parseSummary(inner.summary),
  };
}

function unwrapItems<T>(body: unknown): T[] {
  if (!body || typeof body !== 'object') return [];
  const o = body as Record<string, unknown>;
  const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
    string,
    unknown
  >;
  const { items } = inner;
  return Array.isArray(items) ? (items as T[]) : [];
}

function parseInventoryFilterOption(raw: unknown): InventoryFilterOption | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id)) return null;
  const label = String(
    r.label ?? r.name ?? r.title ?? r.brand ?? r.category ?? r.model ?? r.size ?? r.condition ?? ''
  ).trim();
  if (!label) return null;
  return { id, label };
}

function parseInventoryModelOption(raw: unknown): InventoryModelOption | null {
  const base = parseInventoryFilterOption(raw);
  if (!base) return null;
  return base;
}

function parseInventoryConsignorOption(raw: unknown): InventoryConsignorOption | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id)) return null;
  const fullName = String(r.fullName ?? r.name ?? '').trim();
  if (!fullName) return null;
  return { id, fullName };
}

function parseInventoryProductSearchOption(raw: unknown): InventoryProductSearchOption | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id)) return null;
  const thumbnailCandidate = r.thumbnailUrl ?? r.imageUrl ?? r.image ?? r.photoUrl;
  const brandId = Number(r.brandId);
  const categoryId = Number(r.categoryId);
  const modelId = Number(r.modelId);
  return {
    id,
    title: String(r.title ?? '').trim(),
    brandId: Number.isFinite(brandId) ? brandId : null,
    categoryId: Number.isFinite(categoryId) ? categoryId : null,
    modelId: Number.isFinite(modelId) ? modelId : null,
    brand: r.brand != null ? String(r.brand) : null,
    category: r.category != null ? String(r.category) : null,
    model: r.model != null ? String(r.model) : null,
    thumbnailUrl:
      typeof thumbnailCandidate === 'string' && thumbnailCandidate.trim()
        ? thumbnailCandidate.trim()
        : null,
  };
}

export function buildAdminInventoryListQueryParams(
  q: AdminInventoryListQuery
): Record<string, string | number> {
  const p: Record<string, string | number> = {
    page: q.page,
    pageSize: q.pageSize,
    orderBy: q.orderBy,
    orderDirection: q.orderDirection,
  };
  const set = (key: string, v: string | number | undefined) => {
    if (v === undefined || v === '') return;
    p[key] = v;
  };
  set('categoryId', q.categoryId);
  set('brandId', q.brandId);
  set('modelId', q.modelId);
  set('sizeId', q.sizeId);
  set('conditionId', q.conditionId);
  set('productId', q.productId);
  set('name', q.name?.trim());
  set('model', q.model?.trim());
  set('category', q.category?.trim());
  set('brand', q.brand?.trim());
  set('size', q.size?.trim());
  set('condition', q.condition?.trim());
  set('consignor', q.consignor?.trim());
  if (q.consignorId !== undefined && Number.isFinite(q.consignorId)) {
    p.consignorId = q.consignorId;
  }
  set('sku', q.sku?.trim());
  if (q.status) {
    p.status = q.status;
  }
  return p;
}

export function buildAdminInventoryExportQueryParams(
  q: Omit<AdminInventoryListQuery, 'page' | 'pageSize'>
): Record<string, string | number> {
  const p: Record<string, string | number> = {
    orderBy: q.orderBy,
    orderDirection: q.orderDirection,
  };
  const set = (key: string, v: string | number | undefined) => {
    if (v === undefined || v === '') return;
    p[key] = v;
  };
  set('categoryId', q.categoryId);
  set('brandId', q.brandId);
  set('modelId', q.modelId);
  set('sizeId', q.sizeId);
  set('conditionId', q.conditionId);
  set('productId', q.productId);
  set('name', q.name?.trim());
  set('model', q.model?.trim());
  set('category', q.category?.trim());
  set('brand', q.brand?.trim());
  set('size', q.size?.trim());
  set('condition', q.condition?.trim());
  set('consignor', q.consignor?.trim());
  if (q.consignorId !== undefined && Number.isFinite(q.consignorId)) {
    p.consignorId = q.consignorId;
  }
  set('sku', q.sku?.trim());
  if (q.status) {
    p.status = q.status;
  }
  return p;
}

function parseFilenameFromContentDisposition(header: string | undefined): string | null {
  if (!header) return null;
  const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(header);
  return m ? decodeURIComponent(m[1].trim()) : null;
}

export type InventoryListResult =
  | { ok: true; data: AdminInventoryListPayload }
  | { ok: false; error: string };

export async function fetchAdminInventoryList(
  query: AdminInventoryListQuery
): Promise<InventoryListResult> {
  try {
    const { data } = await authClient.get<unknown>('/api/admin/inventory', {
      params: buildAdminInventoryListQueryParams(query),
    });
    return {
      ok: true,
      data: parseListPayload(data, query.page, query.pageSize),
    };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404 ? 'El endpoint de inventario aún no está disponible.' : null) ??
      'No se pudo cargar el inventario.';
    return { ok: false, error: msg };
  }
}

export async function fetchInventoryCategories(): Promise<InventoryFilterOption[]> {
  try {
    const { data } = await authClient.get<unknown>('/api/admin/inventory/filters/categories');
    const items = unwrapItems<unknown>(data);
    return items
      .map(parseInventoryFilterOption)
      .filter((x): x is InventoryFilterOption => x !== null);
  } catch {
    return [];
  }
}

export async function fetchInventoryBrands(): Promise<InventoryFilterOption[]> {
  try {
    const { data } = await authClient.get<unknown>('/api/admin/inventory/filters/brands');
    const items = unwrapItems<unknown>(data);
    return items
      .map(parseInventoryFilterOption)
      .filter((x): x is InventoryFilterOption => x !== null);
  } catch {
    return [];
  }
}

export async function searchInventoryModels(params: {
  q: string;
  categoryId?: number;
  brandId?: number;
}): Promise<InventoryModelOption[]> {
  const query = params.q.trim();
  try {
    const { data } = await authClient.get<unknown>('/api/admin/inventory/filters/models/search', {
      params: {
        q: query || undefined,
        categoryId: params.categoryId,
        brandId: params.brandId,
      },
    });
    const items = unwrapItems<unknown>(data);
    return items
      .map(parseInventoryModelOption)
      .filter((x): x is InventoryModelOption => x !== null);
  } catch {
    return [];
  }
}

export async function fetchInventorySizesByCategory(
  categoryId: number
): Promise<InventoryFilterOption[]> {
  if (!Number.isFinite(categoryId)) return [];
  try {
    const { data } = await authClient.get<unknown>('/api/admin/inventory/filters/sizes', {
      params: { categoryId },
    });
    const items = unwrapItems<unknown>(data);
    return items
      .map(parseInventoryFilterOption)
      .filter((x): x is InventoryFilterOption => x !== null);
  } catch {
    return [];
  }
}

export async function fetchInventoryConditionsByCategory(
  categoryId: number
): Promise<InventoryFilterOption[]> {
  if (!Number.isFinite(categoryId)) return [];
  try {
    const { data } = await authClient.get<unknown>('/api/admin/inventory/filters/conditions', {
      params: { categoryId },
    });
    const items = unwrapItems<unknown>(data);
    return items
      .map(parseInventoryFilterOption)
      .filter((x): x is InventoryFilterOption => x !== null);
  } catch {
    return [];
  }
}

export async function searchInventoryConsignors(q: string): Promise<InventoryConsignorOption[]> {
  const query = q.trim();
  try {
    const { data } = await authClient.get<unknown>(
      '/api/admin/inventory/filters/consignors/search',
      {
        params: { q: query || undefined },
      }
    );
    const items = unwrapItems<unknown>(data);
    return items
      .map(parseInventoryConsignorOption)
      .filter((x): x is InventoryConsignorOption => x !== null);
  } catch {
    return [];
  }
}

export async function searchInventoryProducts(q: string): Promise<InventoryProductSearchOption[]> {
  const query = q.trim();
  if (!query) return [];
  try {
    const { data } = await authClient.get<unknown>('/api/admin/products/search', {
      params: { q: query },
    });
    const items = unwrapItems<unknown>(data);
    return items
      .map(parseInventoryProductSearchOption)
      .filter((x): x is InventoryProductSearchOption => x !== null);
  } catch {
    return [];
  }
}

function parsePhotoId(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function parseImageUrl(raw: Record<string, unknown>): string | null {
  const candidate = raw.url ?? raw.imageUrl ?? raw.thumbnailUrl ?? raw.src;
  if (typeof candidate !== 'string') return null;
  const url = candidate.trim();
  return url || null;
}

function parseInventoryProductPhoto(raw: unknown): InventoryProductPhoto | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = parsePhotoId(r.id);
  const url = parseImageUrl(r);
  if (id == null || !url) return null;
  const angle = typeof r.angle === 'string' && r.angle.trim() ? r.angle.trim() : 'front';
  return { id, angle, url };
}

function parseInventoryExtraPhoto(raw: unknown, index: number): InventoryExtraPhoto | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = parsePhotoId(r.id);
  const url = parseImageUrl(r);
  if (id == null || !url) return null;
  const displayOrderRaw = Number(r.displayOrder ?? r.order ?? index);
  return {
    id,
    url,
    displayOrder: Number.isFinite(displayOrderRaw) ? displayOrderRaw : index,
  };
}

/** Unwrap `{ data: { item } }` (and common variants) to the item object. */
export function unwrapAdminApiItem(body: unknown): Record<string, unknown> | null {
  if (!body || typeof body !== 'object') return null;
  const o = body as Record<string, unknown>;
  const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
    string,
    unknown
  >;
  const item = inner.item ?? inner.product ?? inner.variant ?? inner;
  return item && typeof item === 'object' ? (item as Record<string, unknown>) : null;
}

export interface InventoryVariantConditionPhoto {
  id: number | null;
  angle: string;
  url: string | null;
  displayOrder: number;
}

function parseInventoryVariantConditionPhoto(
  raw: unknown,
  index: number
): InventoryVariantConditionPhoto | null {
  if (typeof raw === 'string' && raw.trim()) {
    return { id: null, angle: `photo_${index}`, url: raw.trim(), displayOrder: index };
  }
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const angleRaw = typeof r.angle === 'string' ? r.angle.trim() : '';
  const angle = angleRaw || `photo_${index}`;
  const idRaw = r.id;
  const id = idRaw == null || idRaw === '' ? null : parsePhotoId(idRaw);
  const url = parseImageUrl(r);
  const displayOrderRaw = Number(r.displayOrder ?? r.order ?? index);
  return {
    id,
    angle,
    url,
    displayOrder: Number.isFinite(displayOrderRaw) ? displayOrderRaw : index,
  };
}

function parseLegacyPhotos(raw: unknown): InventoryLegacyPhoto[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x, i) => parseInventoryExtraPhoto(x, i))
    .filter((x): x is InventoryLegacyPhoto => x !== null)
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
}

export interface ParsedVariantPhotos {
  isUsedVariant: boolean;
  conditionPhotos: InventoryVariantConditionPhoto[];
  productPhotos: InventoryProductPhoto[];
  extraPhotos: InventoryExtraPhoto[];
  legacyPhotos: InventoryLegacyPhoto[];
  thumbnailUrl: string | null;
}

/** Parses mutually exclusive deadstock vs used photo shapes from variant GET item. */
export function parseVariantPhotosFromApiItem(item: Record<string, unknown>): ParsedVariantPhotos {
  const isUsedVariant = Array.isArray(item.conditionPhotos) && item.conditionPhotos.length > 0;

  const conditionPhotos = isUsedVariant
    ? (item.conditionPhotos as unknown[])
        .map((x, i) => parseInventoryVariantConditionPhoto(x, i))
        .filter((x): x is InventoryVariantConditionPhoto => x !== null)
        .sort((a, b) => a.displayOrder - b.displayOrder)
    : [];

  const productPhotos =
    !isUsedVariant && Array.isArray(item.photos)
      ? item.photos
          .map(parseInventoryProductPhoto)
          .filter((x): x is InventoryProductPhoto => x !== null)
      : [];

  const extraRaw = item.extraPhotos ?? item.extra_photos ?? [];
  const extraPhotos = Array.isArray(extraRaw)
    ? extraRaw
        .map((x, i) => parseInventoryExtraPhoto(x, i))
        .filter((x): x is InventoryExtraPhoto => x !== null)
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    : [];

  const legacyPhotos = parseLegacyPhotos(item.legacyPhotos ?? item.legacy_photos);

  const thumbnailUrl = isUsedVariant
    ? null
    : (() => {
        const candidate = item.thumbnailUrl ?? item.imageUrl ?? item.image ?? item.photoUrl;
        return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : null;
      })();

  return {
    isUsedVariant,
    conditionPhotos,
    productPhotos,
    extraPhotos,
    legacyPhotos,
    thumbnailUrl,
  };
}

function parseInventoryEditProductFormData(raw: unknown): InventoryEditProductFormData | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const brandId = Number(r.brandId);
  const categoryId = Number(r.categoryId);
  const modelId = Number(r.modelId);
  if (!Number.isFinite(brandId) || !Number.isFinite(categoryId) || !Number.isFinite(modelId)) {
    return null;
  }
  const thumbnailCandidate = r.thumbnailUrl ?? r.imageUrl ?? r.image ?? r.photoUrl;
  const thumbnailUrl =
    typeof thumbnailCandidate === 'string' && thumbnailCandidate.trim()
      ? thumbnailCandidate.trim()
      : null;
  const photoRaw = r.photos ?? [];
  const photos = Array.isArray(photoRaw)
    ? photoRaw.map(parseInventoryProductPhoto).filter((x): x is InventoryProductPhoto => x !== null)
    : [];
  const extraRaw = r.extraPhotos ?? r.extra_photos ?? [];
  const extraPhotos = Array.isArray(extraRaw)
    ? extraRaw
        .map((x, i) => parseInventoryExtraPhoto(x, i))
        .filter((x): x is InventoryExtraPhoto => x !== null)
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    : [];
  const legacyPhotos = parseLegacyPhotos(r.legacyPhotos ?? r.legacy_photos);
  const modelLabel = String(r.model ?? r.modelName ?? '').trim();
  const colorway = r.colorway == null ? '' : String(r.colorway);
  const sku = r.sku == null ? '' : String(r.sku);
  const description = r.description == null ? '' : String(r.description);
  const productIdRaw = Number(r.productId ?? r.id);
  const frontUrl =
    photos.find((p) => p.angle === 'front')?.url ?? thumbnailUrl ?? legacyPhotos[0]?.url ?? null;
  return {
    productId: Number.isFinite(productIdRaw) && productIdRaw > 0 ? productIdRaw : null,
    brandId,
    categoryId,
    modelId,
    modelLabel,
    colorway,
    sku,
    description,
    imageUrl: frontUrl,
    thumbnailUrl,
    photos,
    extraPhotos,
    legacyPhotos,
  };
}

export type InventoryEditProductLoadResult =
  | { ok: true; data: InventoryEditProductFormData }
  | { ok: false; error: string };

export async function fetchInventoryProductForEdit(
  inventoryId: number
): Promise<InventoryEditProductLoadResult> {
  if (!Number.isFinite(inventoryId)) {
    return { ok: false, error: 'ID de inventario inválido.' };
  }
  try {
    const { data } = await authClient.get<unknown>(`/api/admin/inventory/${inventoryId}/product`);
    const parsed = (() => {
      const item = unwrapAdminApiItem(data);
      return item ? parseInventoryEditProductFormData(item) : null;
    })();
    if (!parsed) {
      return { ok: false, error: 'No se pudo leer el producto para edición.' };
    }
    return { ok: true, data: parsed };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404 ? 'El endpoint de edición no está disponible.' : null) ??
      'No se pudo cargar el producto para edición.';
    return { ok: false, error: msg };
  }
}

export interface InventoryEditProductPayload {
  brandId: number;
  categoryId: number;
  modelId: number;
  colorway: string;
  sku: string;
  description: string;
  image?: File | null;
  photoAngle?: 'front' | 'extra';
  replaceImageId?: number;
  deleteImageId?: number;
}

export type InventoryEditProductSubmitResult = { ok: true } | { ok: false; error: string };

export interface ProductSalesChannel {
  id: string;
  name: string;
  published: boolean;
}

export interface ProductSalesChannelsState {
  posted: boolean;
  canEnable: boolean;
  channels: ProductSalesChannel[];
}

function parseProductSalesChannelsState(raw: unknown): ProductSalesChannelsState | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const list = Array.isArray(r.channels) ? r.channels : [];
  const channels = list
    .map((item): ProductSalesChannel | null => {
      if (!item || typeof item !== 'object') return null;
      const c = item as Record<string, unknown>;
      const id = String(c.id ?? '').trim();
      const name = String(c.name ?? '').trim();
      if (!id) return null;
      return { id, name: name || id, published: parseBooleanish(c.published) };
    })
    .filter((x): x is ProductSalesChannel => x !== null);
  return {
    posted: parseBooleanish(r.posted),
    canEnable: parseBooleanish(r.canEnable),
    channels,
  };
}

export type ProductSalesChannelsLoadResult =
  | { ok: true; data: ProductSalesChannelsState }
  | { ok: false; error: string };

export async function fetchProductSalesChannels(
  inventoryId: number
): Promise<ProductSalesChannelsLoadResult> {
  if (!Number.isFinite(inventoryId)) {
    return { ok: false, error: 'ID de inventario inválido.' };
  }
  try {
    const { data } = await authClient.get<unknown>(
      `/api/admin/inventory/${inventoryId}/product/sales-channels`
    );
    const inner =
      data && typeof data === 'object' && 'data' in data ? (data as { data: unknown }).data : data;
    const parsed = parseProductSalesChannelsState(inner);
    if (!parsed) {
      return { ok: false, error: 'No se pudieron leer los canales de venta.' };
    }
    return { ok: true, data: parsed };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudieron cargar los canales de venta.',
    };
  }
}

export type ProductSalesChannelToggleResult =
  | { ok: true; data: ProductSalesChannelsState }
  | { ok: false; error: string };

export async function setProductSalesChannel(
  inventoryId: number,
  publicationId: string,
  published: boolean
): Promise<ProductSalesChannelToggleResult> {
  if (!Number.isFinite(inventoryId) || !publicationId.trim()) {
    return { ok: false, error: 'Canal inválido.' };
  }
  try {
    const { data } = await authClient.patch<unknown>(
      `/api/admin/inventory/${inventoryId}/product/sales-channels`,
      { publicationId, published }
    );
    const inner =
      data && typeof data === 'object' && 'data' in data ? (data as { data: unknown }).data : data;
    const parsed = parseProductSalesChannelsState(inner);
    if (!parsed) {
      return { ok: false, error: 'El canal se actualizó, pero la respuesta no fue válida.' };
    }
    return { ok: true, data: parsed };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudo actualizar el canal de venta.',
    };
  }
}

export async function updateInventoryProduct(
  inventoryId: number,
  payload: InventoryEditProductPayload
): Promise<InventoryEditProductSubmitResult> {
  if (!Number.isFinite(inventoryId)) {
    return { ok: false, error: 'ID de inventario inválido.' };
  }
  try {
    const fd = new FormData();
    fd.append('brandId', String(payload.brandId));
    fd.append('categoryId', String(payload.categoryId));
    fd.append('modelId', String(payload.modelId));
    fd.append('colorway', payload.colorway.trim());
    fd.append('sku', payload.sku.trim().toUpperCase());
    fd.append('description', payload.description.trim());
    if (payload.photoAngle) {
      fd.append('photoAngle', payload.photoAngle);
    }
    if (payload.replaceImageId != null) {
      fd.append('replaceImageId', String(payload.replaceImageId));
    }
    if (payload.deleteImageId != null) {
      fd.append('deleteImageId', String(payload.deleteImageId));
    }
    if (payload.image) {
      fd.append('image', payload.image);
    }
    await authClient.put(`/api/admin/inventory/${inventoryId}/product`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { ok: true };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404 ? 'El endpoint de actualización no está disponible.' : null) ??
      'No se pudo actualizar el producto.';
    return { ok: false, error: msg };
  }
}

export interface InventoryVariantEditData {
  inventoryId: number;
  productId: number | null;
  title: string;
  brand: string | null;
  category: string | null;
  categoryId: number | null;
  sku: string | null;
  status: InventoryStatus;
  comments: string;
  consignorId: number | null;
  sizeId: number | null;
  conditionId: number | null;
  conditionLabel: string | null;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  cost: number;
  publicPrice: number;
  shopifyActive: boolean;
  clean: boolean;
  isUsedVariant: boolean;
  conditionPhotos: InventoryVariantConditionPhoto[];
  productPhotos: InventoryProductPhoto[];
  extraPhotos: InventoryExtraPhoto[];
  legacyPhotos: InventoryLegacyPhoto[];
}

function parseInventoryVariantEditData(raw: unknown): InventoryVariantEditData | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const inventoryId = Number(r.inventoryId ?? r.id);
  if (!Number.isFinite(inventoryId)) return null;
  const productId = Number(r.productId);
  const categoryId = Number(r.categoryId);
  const consignorId = Number(r.consignorId);
  const sizeId = Number(r.sizeId);
  const conditionId = Number(r.conditionId);
  const cost = Number(r.cost);
  const publicPrice = Number(r.publicPrice ?? r.price);

  const photoFields = parseVariantPhotosFromApiItem(r);

  let conditionLabel: string | null = null;
  if (typeof r.condition === 'string' && r.condition.trim()) {
    conditionLabel = r.condition.trim();
  } else if (typeof r.conditionLabel === 'string' && r.conditionLabel.trim()) {
    conditionLabel = r.conditionLabel.trim();
  }

  const frontProductUrl = photoFields.productPhotos.find((p) => p.angle === 'front')?.url ?? null;
  const firstConditionUrl =
    photoFields.conditionPhotos.find((p) => p.url)?.url ?? photoFields.legacyPhotos[0]?.url ?? null;

  return {
    inventoryId,
    productId: Number.isFinite(productId) ? productId : null,
    title: String(r.title ?? '').trim(),
    brand: r.brand == null ? null : String(r.brand),
    category: r.category == null ? null : String(r.category),
    categoryId: Number.isFinite(categoryId) ? categoryId : null,
    sku: r.sku == null ? null : String(r.sku),
    status: parseStatus(r.status),
    comments: r.comments == null ? '' : String(r.comments),
    consignorId: Number.isFinite(consignorId) ? consignorId : null,
    sizeId: Number.isFinite(sizeId) ? sizeId : null,
    conditionId: Number.isFinite(conditionId) ? conditionId : null,
    conditionLabel,
    imageUrl: photoFields.isUsedVariant
      ? firstConditionUrl
      : (frontProductUrl ?? photoFields.thumbnailUrl),
    thumbnailUrl: photoFields.thumbnailUrl,
    cost: Number.isFinite(cost) ? cost : 0,
    publicPrice: Number.isFinite(publicPrice) ? publicPrice : 0,
    shopifyActive: parseBooleanish(
      r.shopifyActive ?? r.isShopifyActive ?? r.shopify_active ?? r.is_shopify_active
    ),
    clean: parseBooleanish(r.clean ?? r.isClean ?? r.is_clean ?? true),
    isUsedVariant: photoFields.isUsedVariant,
    conditionPhotos: photoFields.conditionPhotos,
    productPhotos: photoFields.productPhotos,
    extraPhotos: photoFields.extraPhotos,
    legacyPhotos: photoFields.legacyPhotos,
  };
}

export type InventoryVariantLoadResult =
  | { ok: true; data: InventoryVariantEditData }
  | { ok: false; error: string };

export async function fetchInventoryVariantForEdit(
  inventoryId: number
): Promise<InventoryVariantLoadResult> {
  if (!Number.isFinite(inventoryId)) {
    return { ok: false, error: 'ID de inventario inválido.' };
  }
  try {
    const { data } = await authClient.get<unknown>(`/api/admin/inventory/${inventoryId}/variant`);
    const parsed = (() => {
      const item = unwrapAdminApiItem(data);
      return item ? parseInventoryVariantEditData(item) : null;
    })();
    if (!parsed) {
      return { ok: false, error: 'No se pudo cargar la variante para edición.' };
    }
    return { ok: true, data: parsed };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404
        ? 'El endpoint de edición de variante no está disponible.'
        : null) ??
      'No se pudo cargar la variante.';
    return { ok: false, error: msg };
  }
}

export interface InventoryVariantUpdatePayload {
  comments: string;
  consignorId: number;
  sizeId: number;
  conditionId: number;
  cost: number;
  publicPrice: number;
}

export type InventoryVariantUpdateResult = { ok: true } | { ok: false; error: string };

export async function updateInventoryVariant(
  inventoryId: number,
  payload: InventoryVariantUpdatePayload
): Promise<InventoryVariantUpdateResult> {
  if (!Number.isFinite(inventoryId)) {
    return { ok: false, error: 'ID de inventario inválido.' };
  }
  try {
    await authClient.put(`/api/admin/inventory/${inventoryId}/variant`, {
      comments: payload.comments.trim(),
      consignorId: payload.consignorId,
      sizeId: payload.sizeId,
      conditionId: payload.conditionId,
      cost: payload.cost,
      publicPrice: payload.publicPrice,
    });
    return { ok: true };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404
        ? 'El endpoint para guardar variante no está disponible.'
        : null) ??
      'No se pudo guardar la variante.';
    return { ok: false, error: msg };
  }
}

export type InventoryVariantStatusAction = 'activate' | 'deactivate' | 'markReturned';

export type InventoryVariantStatusActionResult = { ok: true } | { ok: false; error: string };

export async function runInventoryVariantQuickAction(
  inventoryId: number,
  action: InventoryVariantStatusAction
): Promise<InventoryVariantStatusActionResult> {
  if (!Number.isFinite(inventoryId)) {
    return { ok: false, error: 'ID de inventario inválido.' };
  }
  try {
    await authClient.patch(`/api/admin/inventory/${inventoryId}/status`, { action });
    return { ok: true };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404
        ? 'El endpoint de acciones rápidas no está disponible.'
        : null) ??
      'No se pudo aplicar la acción rápida.';
    return { ok: false, error: msg };
  }
}

export type InventoryPhotoUploadResult = { ok: true; url: string } | { ok: false; error: string };

export type InventoryPhotoDeleteResult = { ok: true } | { ok: false; error: string };

function parseUploadedPhotoUrl(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const o = data as Record<string, unknown>;
  const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
    string,
    unknown
  >;
  const candidate = inner.url ?? inner.imageUrl ?? inner.photoUrl;
  const url = typeof candidate === 'string' ? candidate.trim() : '';
  return url || null;
}

export async function uploadInventoryProductPhoto(
  inventoryId: number,
  angle: 'front' | 'extra',
  file: File,
  replaceImageId?: number | null
): Promise<InventoryPhotoUploadResult> {
  if (!Number.isFinite(inventoryId)) {
    return { ok: false, error: 'ID de inventario inválido.' };
  }
  try {
    const fd = new FormData();
    fd.append('image', file);
    if (replaceImageId != null) {
      fd.append('replaceImageId', String(replaceImageId));
    }
    const { data } = await authClient.post<unknown>(
      `/api/admin/inventory/${inventoryId}/product/photos/${angle}`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    const url = parseUploadedPhotoUrl(data);
    if (!url) return { ok: false, error: 'La respuesta no incluye la URL de la imagen.' };
    return { ok: true, url };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404 ? 'El endpoint de subida de foto no está disponible.' : null) ??
      'No se pudo subir la foto del producto.';
    return { ok: false, error: msg };
  }
}

export async function deleteInventoryProductPhoto(
  inventoryId: number,
  imageId: number
): Promise<InventoryPhotoDeleteResult> {
  if (!Number.isFinite(inventoryId) || !Number.isFinite(imageId)) {
    return { ok: false, error: 'ID inválido.' };
  }
  try {
    await authClient.delete(`/api/admin/inventory/${inventoryId}/product/photos/${imageId}`);
    return { ok: true };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404 ? 'El endpoint de eliminación no está disponible.' : null) ??
      'No se pudo eliminar la foto del producto.';
    return { ok: false, error: msg };
  }
}

export type InventoryVariantPhotoAngle =
  | 'front_45'
  | 'lateral_exterior'
  | 'lateral_interior'
  | 'back'
  | 'outsole'
  | 'extra';

export async function uploadInventoryVariantPhoto(
  inventoryId: number,
  angle: InventoryVariantPhotoAngle,
  file: File,
  replaceImageId?: number | null
): Promise<InventoryPhotoUploadResult> {
  if (!Number.isFinite(inventoryId)) {
    return { ok: false, error: 'ID de inventario inválido.' };
  }
  try {
    const fd = new FormData();
    fd.append('image', file);
    if (replaceImageId != null) {
      fd.append('replaceImageId', String(replaceImageId));
    }
    const { data } = await authClient.post<unknown>(
      `/api/admin/inventory/${inventoryId}/variant/photos/${angle}`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    const url = parseUploadedPhotoUrl(data);
    if (!url) return { ok: false, error: 'La respuesta no incluye la URL de la imagen.' };
    return { ok: true, url };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404 ? 'El endpoint de subida de foto no está disponible.' : null) ??
      'No se pudo subir la foto de la variante.';
    return { ok: false, error: msg };
  }
}

export async function deleteInventoryVariantPhoto(
  inventoryId: number,
  imageId: number
): Promise<InventoryPhotoDeleteResult> {
  if (!Number.isFinite(inventoryId) || !Number.isFinite(imageId)) {
    return { ok: false, error: 'ID inválido.' };
  }
  try {
    await authClient.delete(`/api/admin/inventory/${inventoryId}/variant/photos/${imageId}`);
    return { ok: true };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404 ? 'El endpoint de eliminación no está disponible.' : null) ??
      'No se pudo eliminar la foto de la variante.';
    return { ok: false, error: msg };
  }
}

export type InventoryExportResult = { ok: true } | { ok: false; error: string };

export async function exportAdminInventoryCsv(
  filters: Omit<AdminInventoryListQuery, 'page' | 'pageSize'>
): Promise<InventoryExportResult> {
  try {
    const res = await authClient.get<Blob>('/api/admin/inventory/export', {
      params: buildAdminInventoryExportQueryParams(filters),
      responseType: 'blob',
    });
    const filename =
      parseFilenameFromContentDisposition(res.headers['content-disposition']) ?? 'inventario.csv';
    const blob = res.data;
    if (blob.size === 0) {
      return { ok: false, error: 'El archivo exportado está vacío.' };
    }
    downloadBlob(blob, filename);
    return { ok: true };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404
        ? 'La exportación aún no está disponible en el servidor.'
        : null) ??
      'No se pudo descargar el CSV.';
    return { ok: false, error: typeof msg === 'string' ? msg : 'No se pudo descargar el CSV.' };
  }
}

// ── Reprint ───────────────────────────────────────────────────────────────────

export async function reprintInventoryLabel(
  inventoryId: number
): Promise<{ ok: true; printJobId: number } | { ok: false; error: string }> {
  try {
    const { data } = await authClient.post<{ data: { printJobId: number } }>(
      `/api/admin/inventory/${inventoryId}/reprint`
    );
    return { ok: true, printJobId: data.data.printJobId };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ?? 'No se pudo enviar la etiqueta a la cola de impresión.';
    return { ok: false, error: msg };
  }
}

// ── Inventory selection (for QR inventory-check feature) ──────────────────────

export interface InventorySelectionItem {
  id: number;
  artId: string | null;
  productTitle: string;
  sku: string | null;
  size: string;
  condition: string;
  status: string;
}

/** Fetches all variants matching the current filters (no pagination) for the inventory-check dialog. */
export async function fetchInventorySelection(
  query: Omit<AdminInventoryListQuery, 'page' | 'pageSize'>
): Promise<{ ok: true; data: InventorySelectionItem[] } | { ok: false; error: string }> {
  try {
    const params = buildAdminInventoryListQueryParams({ ...query, page: 1, pageSize: 9999 });
    // pageSize param is ignored by the /selection endpoint — we pass it just in case
    const { data } = await authClient.get<{ data: InventorySelectionItem[] }>(
      '/api/admin/inventory/selection',
      { params }
    );
    return { ok: true, data: data.data };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg = ax.response?.data?.message ?? 'No se pudo cargar la selección de inventario.';
    return { ok: false, error: msg };
  }
}
